import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpEvent, HttpHandlerFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, from, Observable, ReplaySubject, take } from 'rxjs';
import { TokenService } from '../services/token.service';
import { AuthService } from '../services/auth.service';

const AUTH_ENDPOINTS = [
    '/auth/login',
    '/auth/login-otp',
    '/auth/verify-otp',
    '/auth/refresh',
    '/auth/forgot-password',
    '/auth/reset-password',
] as const;

const HTTP_STATUS = {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    BAD_REQUEST: 400,
    LOCKED: 423,
    SERVER_ERROR_START: 500,
    SERVER_ERROR_END: 599,
} as const;

const ERROR_MESSAGES = {
    SESSION_EXPIRED: 'Session expired',
    REFRESH_TOKEN_INVALID: 'Refresh token invalid or expired. Logging out.',
    REFRESH_TOKEN_EXPIRED: 'Refresh token expired, logging out',
    FORBIDDEN: '403 Forbidden - insufficient permissions for:',
    ACCOUNT_LOCKED: '423 Locked - account locked',
} as const;

const ROUTES = {
    LOGIN: '/auth/login',
} as const;

let isRefreshing = false;

/** Porte d'attente pendant un refresh. ReplaySubject et non BehaviorSubject : il PROPAGE L'ÉCHEC. */
let refreshGate = new ReplaySubject<string>(1);

function isAuthEndpoint(url: string): boolean {
    return AUTH_ENDPOINTS.some(endpoint => url.includes(endpoint));
}

function addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({
        setHeaders: {
            Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
    });
}

function isNetworkError(status: number | undefined): boolean {
    return status === 0 || (status !== undefined && status >= HTTP_STATUS.SERVER_ERROR_START && status <= HTTP_STATUS.SERVER_ERROR_END);
}

function isAuthError(status: number | undefined): boolean {
    return status === HTTP_STATUS.UNAUTHORIZED ||
        status === HTTP_STATUS.FORBIDDEN ||
        status === HTTP_STATUS.BAD_REQUEST;
}

function extractTokenFromResponse(response: unknown): string {
    const tokenData = (response as { data?: { accessToken?: string }; accessToken?: string }).data
        ? (response as { data: { accessToken: string } }).data
        : (response as { accessToken: string });
    return tokenData.accessToken;
}

function handleTokenRefresh(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    tokenService: TokenService
): Observable<HttpEvent<unknown>> {
    if (isRefreshing) {
        // Porte du cycle en cours : un refresh raté la fait rejeter au lieu de suspendre.
        return refreshGate.pipe(
            take(1),
            switchMap((token) => next(addToken(req, token)))
        );
    }

    // Vérifier la connexion réseau avant d'essayer de rafraîchir
    if (!navigator.onLine) {
        console.warn('⚠️ Offline: Cannot refresh token. Request will fail.');
        return throwError(() => new HttpErrorResponse({
            error: 'Network Error',
            status: 0,
            statusText: 'Offline',
            url: req.url,
        }));
    }

    isRefreshing = true;
    refreshGate = new ReplaySubject<string>(1);
    const gate = refreshGate;

    return from(authService.refreshToken()).pipe(
        switchMap((authResponse) => {
            isRefreshing = false;
            const newToken = extractTokenFromResponse(authResponse);
            gate.next(newToken);
            gate.complete();
            return next(addToken(req, newToken));
        }),
        catchError((refreshError: unknown) => {
            isRefreshing = false;

            const httpError = refreshError as HttpErrorResponse;

            // Libérer la file AVEC l'erreur : échouer, pas rester suspendu.
            gate.error(httpError ?? new Error('Unknown refresh error'));
            const status = httpError?.status;

            // Gestion des erreurs réseau
            if (isNetworkError(status) || status === 0 || !navigator.onLine) {
                console.warn('⚠️ Network error during token refresh. Request will fail but session is preserved.');
                return throwError(() => httpError || new HttpErrorResponse({
                    error: 'Network Error',
                    status: 0,
                    statusText: 'Connection Refused',
                    url: req.url,
                }));
            }

            // Gestion des erreurs d'authentification
            if (isAuthError(status)) {
                console.warn(ERROR_MESSAGES.REFRESH_TOKEN_INVALID);
                authService.logout();
            }

            return throwError(() => httpError || new Error('Unknown refresh error'));
        })
    );
}

function checkRefreshTokenExpiration(authService: AuthService, tokenService: TokenService): void {
    tokenService.isRefreshTokenExpired()
        .then((isExpired) => {
            if (isExpired) {
                console.warn(ERROR_MESSAGES.REFRESH_TOKEN_EXPIRED);
                authService.logout();
            }
        })
        .catch(() => {
            // Ignore verification errors
        });
}

function handleExpiredTokenWithoutRefresh(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService
): Observable<HttpEvent<unknown>> {
    // Hors ligne avéré : ne PAS déconnecter, sinon perte d'accès aux données locales.
    if (!navigator.onLine) {
        return next(req);
    }

    authService.logout();
    return throwError(() => new Error(ERROR_MESSAGES.SESSION_EXPIRED));
}

function handleHttpError(
    error: HttpErrorResponse,
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    tokenService: TokenService,
    authService: AuthService,
    router: Router
): Observable<HttpEvent<unknown>> {
    if (error.status === HTTP_STATUS.UNAUTHORIZED) {
        if (tokenService.hasRefreshToken()) {
            return handleTokenRefresh(req, next, authService, tokenService);
        }
        authService.logout();
        return throwError(() => error);
    }

    if (error.status === HTTP_STATUS.FORBIDDEN) {
        console.warn(ERROR_MESSAGES.FORBIDDEN, req.url);
        console.warn('Error details:', error.error);
        return throwError(() => error);
    }

    if (error.status === HTTP_STATUS.LOCKED) {
        console.error(ERROR_MESSAGES.ACCOUNT_LOCKED);
        authService.logout();
        router.navigate([ROUTES.LOGIN], {
            queryParams: { error: 'account_locked' },
        });
        return throwError(() => error);
    }

    return throwError(() => error || new Error('Unknown HTTP Error'));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const injector = inject(Injector);
    const tokenService = inject(TokenService);
    const router = inject(Router);

    const getAuthService = (): AuthService => injector.get(AuthService);

    if (isAuthEndpoint(req.url) || req.url.includes('/assets/i18n/')) {
        return next(req);
    }

    const token = tokenService.getAccessToken();
    if (!token) {
        return next(req);
    }

    if (tokenService.isTokenExpired(token)) {
        if (!tokenService.hasRefreshToken()) {
            return handleExpiredTokenWithoutRefresh(req, next, getAuthService());
        }

        checkRefreshTokenExpiration(getAuthService(), tokenService);
        return handleTokenRefresh(req, next, getAuthService(), tokenService);
    }

    const authReq = addToken(req, token);
    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) =>
            handleHttpError(error, req, next, tokenService, getAuthService(), router)
        )
    );
};
